import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Container, CssBaseline, alpha, useTheme, useMediaQuery, Typography } from '@mui/material';

// Critical CSS inlined
import './innerComponent/styles/navbarmenu.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Lazy load components
const Navbar = lazy(() => import('./Navbar'));
const MobileHeader = lazy(() => import('./mobileLayout/mobileHeader'));
const FooterReserved = lazy(() => import('./innerComponent/FooterReserved'));
const LargeFooter = lazy(() => import('./ProfileLayout/FOOTER/LargeFooter'));
const MobileFooter = lazy(() => import('./mobileLayout/mobileFooter'));
const ScrollToTop = lazy(() => import('../components/other/ScrollToTop'));

const spacerStyles = {
  height: { xs: 0, sm: 80, lg: 74 },
};

const Layout = React.memo(({ children }) => {
  const theme = useTheme();

  // Proper device detection using useMediaQuery
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const backgroundColor = useMemo(() =>
    alpha(theme.palette.backgroundPrimary.main, 0.3),
    [theme]
  );

  // Add preconnect for external resources
  useEffect(() => {
    const links = [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    ];

    links.forEach(link => {
      const linkElement = document.createElement('link');
      Object.entries(link).forEach(([key, value]) => {
        linkElement[key] = value;
      });
      document.head.appendChild(linkElement);
    });
  }, []);

  // Render appropriate header based on device
  const renderHeader = useMemo(() => {
    if (isMobile) {
      return <MobileHeader />;
    }
    return <Navbar />;
  }, [isMobile]);

  // Render appropriate footer based on device
  const renderFooter = useMemo(() => {
    if (isMobile) {
      return <MobileFooter />;
    }
    return <LargeFooter />;
  }, [isMobile]);

  return (
    <React.Fragment>
      <CssBaseline enableColorScheme />

      <Suspense fallback={null}>
        <ScrollToTop />
      </Suspense>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          maxWidth: '100%',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor,
        }}
      >
        {/* Header Section */}
        <Suspense fallback={<Box sx={{ height: isMobile ? 60 : 80 }} />}>
          {renderHeader}
        </Suspense>

        {/* Main Content Section */}
        <Container
          maxWidth={false}
          sx={{
            minHeight: '40vh',
            backgroundColor: 'transparent',
            alignItems: 'center',
            position: 'relative',
            px: { xs: 1, sm: 2, md: 3 },
          }}
          disableGutters={isMobile}
        >
          <Box component="div" sx={spacerStyles} />

          <Suspense fallback={
            <Box sx={{
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              <Typography>Loading...</Typography>
            </Box>
          }>
            {children || <Outlet />}
          </Suspense>
        </Container>

        {/* Footer Section */}
        <Suspense fallback={<Box sx={{ height: isMobile ? 300 : 400 }} />}>
          {renderFooter}
          <FooterReserved />
        </Suspense>
      </Container>

      {/* Add keyframe animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </React.Fragment>
  );
});

Layout.displayName = 'Layout';

export default Layout;
